import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { Rendicontazione } from '../../../models/rendicontazione.model';
import { Intervento } from '../../../models/intervento.model';

@Component({
  selector: 'app-carica-documenti-rendicontazione',
  templateUrl: './carica-documenti-rendicontazione.component.html',
  styleUrls: ['./carica-documenti-rendicontazione.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    ToastModule,
    TooltipModule
  ],
  providers: [MessageService]
})
export class CaricaDocumentiRendicontazioneComponent implements OnInit {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() rendicontazione: Rendicontazione | null = null;
  @Input() intervento: Intervento | null = null;
  @Output() documentiCaricati = new EventEmitter<void>();

  selectedFiles: File[] = [];
  uploading: boolean = false;
  uploadProgress: number = 0;

  constructor(
    private messageService: MessageService,
    private firebaseStorageService: FirebaseStorageService
  ) {}

  ngOnInit() {}

  show(rendicontazione: Rendicontazione, intervento: Intervento) {
    if (!rendicontazione || !intervento) {
      return;
    }

    this.rendicontazione = rendicontazione;
    this.intervento = intervento;
    this.selectedFiles = [];
    this.uploadProgress = 0;
    this.visible = true;
    this.visibleChange.emit(this.visible);
  }

  hide() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.resetForm();
  }

  private resetForm() {
    this.selectedFiles = [];
    this.uploadProgress = 0;
    this.uploading = false;
  }

    onFileSelect(event: any) {
    if (!this.rendicontazione || !this.intervento) {
      return;
    }

    // Gestisci diversi tipi di eventi del p-fileUpload
    if (event.files && Array.isArray(event.files)) {
      // Evento standard con array di file
      this.selectedFiles = [...event.files];
    } else if (event.target && event.target.files) {
      // Evento nativo del browser
      this.selectedFiles = Array.from(event.target.files);
    } else if (event.currentFiles && Array.isArray(event.currentFiles)) {
      // Evento PrimeNG con currentFiles
      this.selectedFiles = [...event.currentFiles];
    } else {
      return;
    }
  }

  onFileClear(event: any) {
    this.selectedFiles = [];
  }

  onFileRemove(event: any) {
    if (event.file) {
      const index = this.selectedFiles.findIndex(f => f.name === event.file.name);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
      }
    }
  }

  removeFile(file: File) {
    const index = this.selectedFiles.indexOf(file);
    if (index > -1) {
      this.selectedFiles.splice(index, 1);
    }
  }

  async uploadSelectedFiles() {
    if (!this.selectedFiles.length || !this.rendicontazione || !this.intervento) return;

    this.uploading = true;
    this.uploadProgress = 0;

    try {
      const totalFiles = this.selectedFiles.length;
      let uploadedFiles = 0;

      for (const file of this.selectedFiles) {
        await this.uploadFile(file);
        uploadedFiles++;
        this.uploadProgress = (uploadedFiles / totalFiles) * 100;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Upload completato',
        detail: `${this.selectedFiles.length} file caricati con successo`,
        life: 5000
      });

      this.documentiCaricati.emit();
      this.hide();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore upload',
        detail: 'Si è verificato un errore durante il caricamento dei file',
        life: 5000
      });
    } finally {
      this.uploading = false;
    }
  }

  private async uploadFile(file: File): Promise<void> {
    if (!this.rendicontazione || !this.intervento) {
      throw new Error('Rendicontazione o intervento non definiti');
    }

    const path = `interventi/${this.intervento.id}/rendicontazioni/${this.rendicontazione.id}`;
    const fileName = `${file.name}`;
    const fullPath = `${path}/${fileName}`;

    return new Promise((resolve, reject) => {
      this.firebaseStorageService.uploadFile(file, fullPath).subscribe({
        next: () => {
          resolve();
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  isFormValid(): boolean {
    return this.rendicontazione !== null &&
           this.intervento !== null &&
           this.selectedFiles.length > 0 &&
           !this.uploading;
  }

  getTotalFileSize(): number {
    return this.selectedFiles.reduce((total, file) => total + file.size, 0);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
